import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Button, Drawer, Grid } from 'antd';
import { LogoutOutlined, MenuOutlined, HomeOutlined, IdcardOutlined, SettingOutlined, StockOutlined, LockOutlined, UserOutlined, GlobalOutlined, DatabaseOutlined, CoffeeOutlined, ToolOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import './Layout.css';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import pkg from '../../package.json';

const { Content, Footer, Sider, Header } = Layout;

const AppLayout = ({ children }) => {
  const { auth, logout } = useContext(AuthContext);
  const { isDark } = useContext(ThemeContext);
  const siderBg = isDark ? '#141414' : '#ffffff';
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const screens = Grid.useBreakpoint();
  // md(768px) 미만은 모바일: 사이드바 대신 상단 바 + 서랍 메뉴
  // (첫 렌더에선 breakpoint 값이 비어 있어 창 너비로 판단 — 모바일에서 사이드바가 깜빡이는 것 방지)
  const isMobile = screens.md === undefined ? window.innerWidth < 768 : !screens.md;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const collapsedSider = collapsed && !isMobile;
  const [menuItems, setMenuItems] = useState([]); // 메뉴 항목을 상태로 관리
  // 하단 버전 표기: 업데이트 관리에서 바꾼 값 (없으면 package.json 버전)
  const [version, setVersion] = useState(pkg.version);
  useEffect(() => {
    axios.get(buildApiUrl('/api/updates/version'))
      .then(({ data }) => { if (data?.version) setVersion(data.version); })
      .catch(() => {});
    const onChange = (e) => setVersion(e.detail);
    window.addEventListener('app-version-changed', onChange);
    return () => window.removeEventListener('app-version-changed', onChange);
  }, []);

  // `updateMenuItems` 함수를 useCallback으로 메모이제이션하여 불필요한 재생성을 방지
  const updateMenuItems = useCallback(() => {
    const collapsed = collapsedSider;
    const BMS_MENU_KEYS = ['bms-dashboard', 'bms-data', 'bms'];
    const permMap = {};
    (auth?.menuPermissions || []).forEach(p => { permMap[p.menu_key] = p; });

    const hasViewPermission = (permission) => {
      if (!permission) return false;
      const value = permission.can_view;
      return value === 1 || value === '1' || value === true || value === 'true';
    };

    const canView = (keyOrKeys) => {
      const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
      return keys.some((key) => hasViewPermission(permMap[key]));
    };

    const canViewAny = (keys) => keys.some((key) => canView(key));

    const makeChild = (key, label, path) => ({
      key,
      label: <Link to={path}>{label}</Link>,
    });

    // 원래 메뉴 순서 고정
    const items = [
      {
        key: 'home',
        icon: <HomeOutlined />,
        label: <Link to="/main">{!collapsed && '홈'}</Link>,
      },
    ];

    if (canViewAny(['overtime', 'commute', 'overtime-input'])) {
      const commuteChildren = [];
      if (canView('overtime')) {
        commuteChildren.push(makeChild('attendance.overtime', '야근 보고서', '/attendance/report'));
      }
      if (canView('commute')) {
        commuteChildren.push(makeChild('attendance.commute', '출퇴근 정보', '/attendance/info'));
      }
      if (canView('overtime-input')) {
        commuteChildren.push(makeChild('attendance.overtime-input', '근무시간 입력', '/attendance/overtime-input'));
      }
      if (canView('overtime')) {
        commuteChildren.push(makeChild('attendance.work-hours', '주간 근무시간', '/attendance/work-hours'));
      }

      if (commuteChildren.length > 0) {
        items.push({
          key: 'attendance',
          icon: <IdcardOutlined />,
          label: !collapsed ? '근태관리' : '',
          children: commuteChildren,
        });
      }
    }

    if (canView('employees')) {
      items.push({
        key: 'production',
        icon: <UserOutlined />,
        label: !collapsed ? '생산관리' : '',
        children: [
          makeChild('production.report', '생산 보고서', '/product/report'),
          makeChild('production.people', '인원 관리', '/product/people'),
        ],
      });
    }

    // 식사관리 — 식사 관리 + 카톡 야근 식사봇
    if (canViewAny(['meal', 'kakao-meal-bot'])) {
      const mealChildren = [];
      if (canView('meal')) {
        mealChildren.push(makeChild('meal.manage', '식사 관리', '/meal'));
      }
      if (canView('kakao-meal-bot')) {
        mealChildren.push(makeChild('meal.kakao-bot', '카톡 야근 식사봇', '/admin/kakao-bot'));
      }
      if (mealChildren.length > 0) {
        items.push({
          key: 'meal-group',
          icon: <CoffeeOutlined />,
          label: !collapsed ? '식사관리' : '',
          children: mealChildren,
        });
      }
    }

    if (canViewAny(['employees', 'roman', ...BMS_MENU_KEYS])) {
      const completeWindowChildren = [];
      if (canView(BMS_MENU_KEYS)) {
        completeWindowChildren.push(makeChild('complete-window.bms-data', 'BMS데이터', '/product/bms-data'));
      }
      if (canView('roman')) {
        completeWindowChildren.push(makeChild('complete-window.roman-calc', '로만 사이즈 계산기', '/product/rm-calc'));
        completeWindowChildren.push(makeChild('complete-window.roman-set', '로만 설정', '/product/rm-set'));
      }

      if (completeWindowChildren.length > 0) {
        items.push({
          key: 'complete-window',
          icon: <UserOutlined />,
          label: !collapsed ? '완성창' : '',
          children: completeWindowChildren,
        });
      }
    }

    // 생산도구 — 배송요약(EBMS 납품일지 + 포털 제품타입)
    if (canView('delivery-summary')) {
      items.push({
        key: 'production-tools',
        icon: <ToolOutlined />,
        label: !collapsed ? '생산도구' : '',
        children: [
          makeChild('production-tools.delivery-summary', '배송요약', '/production-tools/delivery-summary'),
        ],
      });
    }

    if (canViewAny(['orders', 'capa'])) {
      const domesticChildren = [];
      if (canView('orders')) {
        domesticChildren.push(makeChild('domestic.orders', '발주량', '/domestic/order_volume'));
      }
      if (canView('capa')) {
        domesticChildren.push(makeChild('domestic.capa', 'CAPA', '/domestic/capa'));
      }
      if (canView('orders')) {
        domesticChildren.push(makeChild('domestic.hanssem-mall', '한샘몰', '/hanssem-mall'));
      }

      if (domesticChildren.length > 0) {
        items.push({
          key: 'domestic',
          icon: <StockOutlined />,
          label: !collapsed ? '국내영업' : '',
          children: domesticChildren,
        });
      }
    }

    if (canView('vendor-management')) {
      items.push({
        key: 'overseas',
        icon: <GlobalOutlined />,
        label: !collapsed ? '해외영업' : '',
        children: [
          makeChild('overseas.finish-window-info', '완성창 정보 입력', '/overseas/finish-window-info'),
          makeChild('overseas.finish-window-order', '완성창 발주 조회', '/overseas/finish-window-order'),
          makeChild('overseas.vendor-management', '거래처 관리', '/overseas/vendor-management'),
        ],
      });
    }

    if (canView('stock-hanssem')) {
      items.push({
        key: 'stock',
        icon: <DatabaseOutlined />,
        label: !collapsed ? '재고관리' : '',
        children: [
          makeChild('stock.hanssem', '한샘', '/stock/hanssem'),
        ],
      });
    }

    if (canView('auth-admin')) {
      items.push({
        key: 'permission',
        icon: <LockOutlined />,
        label: !collapsed ? '권한 관리' : '',
        children: [
          makeChild('permission.group-menu', '그룹·메뉴 권한', '/admin/permissions'),
          makeChild('permission.updates', '업데이트 관리', '/admin/updates'),
        ],
      });
    }

    items.push({
      key: 'settings',
      icon: <SettingOutlined />,
      label: !collapsed ? '설정' : '',
      children: [
        makeChild('settings.page', '설정 페이지', '/settings'),
      ],
    });

    setMenuItems(items);
  }, [collapsedSider, auth]);


  // 메뉴 업데이트 함수를 초기 로딩 시 실행
  useEffect(() => {
    updateMenuItems();
  }, [updateMenuItems]);

  // 모바일 서랍은 페이지 이동 시 닫는다
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menu = (
    <Menu theme={isDark ? 'dark' : 'light'} defaultSelectedKeys={['1']} mode="inline" style={{ backgroundColor: siderBg, borderInlineEnd: 'none' }} items={menuItems} />
  );

  const logoutButton = (
    <div className="logout-button-container">
      <Button
        type="primary"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        style={{ width: '90%', backgroundColor: '#1890ff', color: 'white', border: 'none' }}
      >
        {!collapsedSider && '로그아웃'}
      </Button>
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={260}
          closable={false}
          className="mobile-nav-drawer"
          styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: siderBg } }}
        >
          <div>{menu}</div>
          {logoutButton}
        </Drawer>
      ) : (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={{ backgroundColor: siderBg, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
        >
          <div>
            <div className="logo" />
            {menu}
          </div>
          {logoutButton}
        </Sider>
      )}
      <Layout className="site-layout">
        {isMobile && (
          <Header className="mobile-header" style={{ backgroundColor: siderBg }}>
            <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} aria-label="메뉴 열기" />
            <Link to="/main" className="mobile-header-title">WINTEC HR</Link>
          </Header>
        )}
        <Content className="site-content">
          <div className="site-layout-background site-content-inner">
            {children}
          </div>
        </Content>
        <Footer className="site-footer">현재 개발 진행중입니다. <br/>오류가 있을 수 있습니다. <br/>V{version}</Footer>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
